import type {
  FilterFieldDefinition,
  FilterItem,
} from "@components/TableFilters/FiltersPanel/FiltersPanel.types";
import type { DisputeFilterRequest } from "@services/disputes/types";
import type { TFunction } from "i18next";
import { DISPUTE_STATUSES } from "./OperationsDisputes.mapper";

const LANG_BASE = "vessel-call.operations";

export interface DisputeTerminalOption {
  value: string;
  label: string;
}

export const DISPUTES_FILTER_FIELDS = Object.freeze({
  DISPUTE_REQUEST_NUMBER: "disputeRequestNumbers",
  TERMINAL: "terminal",
  DISPUTE_DETAILS: "disputeDetails",
  CREATED_BY: "createdBy",
  CREATED_AT: "createdAt",
  STATUS: "status",
});

export const getDisputeStatusOptions = (t: TFunction) =>
  Object.values(DISPUTE_STATUSES).map((status) => ({
    value: status,
    label: t(`${LANG_BASE}.status-labels.${status}`),
  }));

export const getDisputeFilterFields = (
  t: TFunction,
  terminalOptions: DisputeTerminalOption[],
): Record<string, FilterFieldDefinition> => ({
  //ID filter is matching the dispute request number
  [DISPUTES_FILTER_FIELDS.DISPUTE_REQUEST_NUMBER]: {
    label: t(`${LANG_BASE}.grid.id`),
    type: "integer",
    minLength: 1,
  },
  [DISPUTES_FILTER_FIELDS.TERMINAL]: {
    label: t(`${LANG_BASE}.grid.terminal`),
    type: "autocomplete",
    options: terminalOptions,
  },
  [DISPUTES_FILTER_FIELDS.CREATED_BY]: {
    label: t(`${LANG_BASE}.grid.submitted-by`),
    type: "text",
    minLength: 1,
  },
  [DISPUTES_FILTER_FIELDS.CREATED_AT]: {
    label: t(`${LANG_BASE}.grid.submission-date`),
    type: "dateFromToWithoutTime",
  },
  [DISPUTES_FILTER_FIELDS.STATUS]: {
    label: t(`${LANG_BASE}.grid.status`),
    type: "autocomplete",
    options: getDisputeStatusOptions(t),
  },
});

const appendStringFilter = (
  payload: DisputeFilterRequest,
  field: "terminal" | "createdBy" | "status",
  value: string,
) => {
  const currentValues = payload[field] ?? [];
  if (!currentValues.includes(value)) {
    payload[field] = [...currentValues, value];
  }
};

const toIsoDateOrNull = (value?: string) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

const appendDateFilter = (payload: DisputeFilterRequest, value: { from?: string; to?: string }) => {
  const nextValues: [string | null, string | null] = [
    toIsoDateOrNull(value?.from),
    toIsoDateOrNull(value?.to),
  ];
  const currentValues = payload.createdAt ?? [];

  if (!currentValues.some(([from, to]) => from === nextValues[0] && to === nextValues[1])) {
    payload.createdAt = [...currentValues, nextValues];
  }
};

const appendIdFilter = (payload: DisputeFilterRequest, value: number) => {
  const currentValues = payload.disputeRequestNumbers ?? [];
  if (!currentValues.includes(value)) {
    payload.disputeRequestNumbers = [...currentValues, value];
  }
};

const isStringFilterField = (field: string): field is "terminal" | "createdBy" | "status" =>
  field === DISPUTES_FILTER_FIELDS.TERMINAL ||
  field === DISPUTES_FILTER_FIELDS.CREATED_BY ||
  field === DISPUTES_FILTER_FIELDS.STATUS;

export const buildDisputeFilterPayload = (
  vesselCallId: string,
  filters: FilterItem[],
): DisputeFilterRequest => {
  const payload: DisputeFilterRequest = { vesselCallId };

  for (const filter of filters) {
    if (!filter.field || filter.value == null || filter.value === "") continue;

    if (
      filter.field === DISPUTES_FILTER_FIELDS.DISPUTE_REQUEST_NUMBER &&
      typeof filter.value === "string"
    ) {
      const value = Number(filter.value.trim());
      if (Number.isInteger(value)) {
        appendIdFilter(payload, value);
      }
      continue;
    }

    if (filter.field === DISPUTES_FILTER_FIELDS.CREATED_AT && typeof filter.value === "object") {
      appendDateFilter(payload, filter.value);
      continue;
    }

    if (isStringFilterField(filter.field) && typeof filter.value === "string") {
      const value = filter.value.trim();
      if (value) {
        appendStringFilter(payload, filter.field, value);
      }
    }
  }

  return payload;
};
